"use client";

import type { Tour } from "@/types";
import { useEffect, useRef, useState } from "react";
import PastTourCard from "../cards/PastTourCard";

function extractDescription(text: string | undefined): string {
  if (!text) return "";
  const index = text.indexOf("–");
  return index !== -1 ? text.substring(index + 1).trim() : text;
}

type PastToursSliderProps = {
  tours: Tour[];
};

export default function PastToursSlider({ tours }: PastToursSliderProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const cardWidthRef = useRef<number | null>(null);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  const checkScrollable = () => {
    const el = scrollRef.current;
    if (el) {
      const isOverflowing = el.scrollWidth > el.clientWidth + 5;
      setIsScrollable(isOverflowing);
    }
  };

  const scroll = (direction: -1 | 1) => {
    if (!scrollRef.current) return;
    const cardWidth = cardWidthRef.current;
    if (!cardWidth) return;
    scrollRef.current.scrollBy({
      left: direction * cardWidth,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const firstChild = el.children[0] as HTMLElement | undefined;
    if (firstChild) {
      cardWidthRef.current = firstChild.offsetWidth + 24;
    }

    const handleScroll = () => {
      if (!scrollRef.current) return;
      const cardWidth = cardWidthRef.current;
      if (!cardWidth) return;
      const index = Math.round(scrollRef.current.scrollLeft / cardWidth);
      setScrollIndex(index);
    };

    el.addEventListener("scroll", handleScroll);
    checkScrollable();

    const resizeObserver = new ResizeObserver(() => {
      checkScrollable();
    });

    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [tours]);

  return (
    <div className='relative max-w-300 text-left py-10 w-full'>
      {isScrollable && (
        <>
          <button
            onClick={() => scroll(-1)}
            aria-label='Scroll past tours left'
            className='absolute -left-12.5 top-1/2 transform -translate-y-1/2 hidden lg:flex w-10 h-10 bg-[#A3A3A3] cursor-pointer hover:bg-black bg-opacity-50 text-white rounded-full items-center justify-center transition hover:bg-opacity-80 z-10'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              aria-hidden='true'
            >
              <polyline points='15 18 9 12 15 6' />
            </svg>
          </button>

          <button
            onClick={() => scroll(1)}
            aria-label='Scroll past tours right'
            className='absolute -right-12.5 top-1/2 transform -translate-y-1/2 hidden lg:flex w-10 h-10 bg-[#A3A3A3] cursor-pointer hover:bg-black bg-opacity-50 text-white rounded-full items-center justify-center transition hover:bg-opacity-80 z-10'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              aria-hidden='true'
            >
              <polyline points='9 18 15 12 9 6' />
            </svg>
          </button>
        </>
      )}

      <div className='relative overflow-hidden'>
        <div
          ref={scrollRef}
          className={`flex gap-6 mask-[linear-gradient(to_right,transparent_0px,black_50px,black_calc(100%-75px),transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_0px,black_50px,black_calc(100%-75px),transparent_100%)] mask-no-repeat [-webkit-mask-repeat:no-repeat] mask-size-[100%_100%] [-webkit-mask-size:100%_100%] overflow-x-auto scroll-smooth no-scrollbar px-6 md:px-10 ${
            isScrollable ? "" : ""
          }`}
        >
          {tours.map((tour: Tour) => {
            const slug = tour.url.split("/").pop();
            const key = slug || tour.url;
            return (
              <PastTourCard
                key={key}
                slug={slug}
                image={tour.header_image}
                title={tour.title}
                description={extractDescription(tour.long_title)}
                tag={tour.tag}
                days={tour.duration}
                date={tour.dates}
              />
            );
          })}
        </div>
      </div>

      {isScrollable && (
        <div className='flex justify-center px-6 mt-4'>
          {tours.map((tour: Tour, index: number) => {
            const key = tour.url;
            return (
              <div
                key={key}
                className={`w-2.5 h-2.5 mx-1 rounded-full transition ${
                  scrollIndex === index
                    ? "bg-gray-600 w-3 h-3"
                    : "bg-gray-400 opacity-50"
                }`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
